import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Power } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import tenantsService from '../services/tenants';
import appsService from '../services/apps';
import useAppUsers, { ALL_APPS } from '../hooks/useAppUsers';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import DataTable, { CopyableId, RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS, formatDate } from '../components/common/filters';
import useServerTable from '../hooks/useServerTable';
import { Typeahead } from '../components/ui/Typeahead';
import { NewUserForm } from '../components/NewUserForm';
import { Field } from '../components/ui/Field';
import { TextInput, Textarea } from '../components/ui/Input';

const configText = (config) => (typeof config === 'string' ? config : JSON.stringify(config || {}));

const MFA_CHANNEL_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
];

/**
 * Structured per-tenant overrides (OTP channels, active-user limit, SSO). Controlled off the
 * tenant_config JSON string — every change re-stringifies the whole config so the raw JSON
 * textarea below stays in sync and unrelated keys are preserved. A key that is left at "inherit"
 * is deleted from tenant_config so the app-level value applies (config-scope principle).
 */
const TenantOverridesEditor = ({ tenantConfigStr, onChange, appId, tenantId }) => {
    const [seatUsage, setSeatUsage] = React.useState(null);
    React.useEffect(() => {
        let active = true;
        if (appId && tenantId) {
            appsService
                .getSeatUsage(appId, tenantId)
                .then((u) => {
                    if (active) setSeatUsage(u);
                })
                .catch(() => {
                    /* non-fatal */
                });
        }
        return () => {
            active = false;
        };
    }, [appId, tenantId]);

    let config = {};
    let parseFailed = false;
    try {
        config = tenantConfigStr ? JSON.parse(tenantConfigStr) : {};
    } catch (e) {
        parseFailed = true;
    }
    // Invalid JSON in the raw textarea — hide the structured editor and let the textarea drive.
    if (parseFailed || config === null || typeof config !== 'object') return null;

    const commit = (next) => onChange(JSON.stringify(next, null, 2));
    const overrideChannels = Array.isArray(config.mfa_channels);

    const toggleOverrideChannels = () => {
        const next = { ...config };
        if (overrideChannels) {
            delete next.mfa_channels;
            delete next.mfa_default_channel;
        } else {
            next.mfa_channels = ['email'];
        }
        commit(next);
    };

    const toggleChannel = (value) => {
        const current = Array.isArray(config.mfa_channels) ? config.mfa_channels : [];
        const arr = current.includes(value) ? current.filter((c) => c !== value) : [...current, value];
        const next = { ...config, mfa_channels: arr };
        if (next.mfa_default_channel && !arr.includes(next.mfa_default_channel)) {
            delete next.mfa_default_channel;
        }
        commit(next);
    };

    const setDefaultChannel = (value) => commit({ ...config, mfa_default_channel: value });

    const setLimit = (value) => {
        const next = { ...config };
        if (value === '') {
            delete next.max_active_users;
        } else {
            next.max_active_users = Number(value);
        }
        commit(next);
    };

    const ssoState = Object.prototype.hasOwnProperty.call(config, 'sso_enabled')
        ? config.sso_enabled
            ? 'on'
            : 'off'
        : 'inherit';
    const setSso = (state) => {
        const next = { ...config };
        if (state === 'inherit') {
            delete next.sso_enabled;
        } else {
            next.sso_enabled = state === 'on';
        }
        commit(next);
    };

    return (
        <div className="space-y-4 rounded-xl border border-slate-100 p-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700">Tenant Overrides</h4>
                {seatUsage && (
                    <span className="text-xs text-slate-500">
                        Seats{' '}
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                            {seatUsage.used} / {seatUsage.limit === null ? '∞' : seatUsage.limit}
                        </span>
                    </span>
                )}
            </div>

            {/* OTP channels */}
            <div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={overrideChannels} onChange={toggleOverrideChannels} />
                    Override OTP channels for this tenant
                </label>
                {overrideChannels && (
                    <div className="mt-2 pl-6">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {MFA_CHANNEL_OPTIONS.map((opt) => {
                                const selected = (config.mfa_channels || []).includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => toggleChannel(opt.value)}
                                        className={`px-3 py-1 rounded-full text-sm border ${
                                            selected
                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        {(config.mfa_channels || []).length > 1 && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Default</span>
                                <select
                                    value={config.mfa_default_channel || (config.mfa_channels || [])[0] || 'email'}
                                    onChange={(e) => setDefaultChannel(e.target.value)}
                                    className="rounded border border-slate-200 px-2 py-1 text-sm"
                                >
                                    {(config.mfa_channels || []).map((c) => {
                                        const o = MFA_CHANNEL_OPTIONS.find((x) => x.value === c);
                                        return (
                                            <option key={c} value={c}>
                                                {o ? o.label : c}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Active user limit */}
            <div>
                <label className="block text-sm text-slate-600 mb-1">Active User Limit</label>
                <input
                    type="number"
                    min="0"
                    value={config.max_active_users === undefined ? '' : config.max_active_users}
                    onChange={(e) => setLimit(e.target.value)}
                    placeholder="Inherit from app"
                    className="w-40 rounded border border-slate-200 px-2 py-1 text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">Blank = inherit the app-level limit.</p>
            </div>

            {/* SSO tri-state */}
            <div>
                <label className="block text-sm text-slate-600 mb-1">Enterprise SSO</label>
                <select
                    value={ssoState}
                    onChange={(e) => setSso(e.target.value)}
                    className="w-40 rounded border border-slate-200 px-2 py-1 text-sm"
                >
                    <option value="inherit">Inherit from app</option>
                    <option value="on">Enabled</option>
                    <option value="off">Disabled</option>
                </select>
            </div>
        </div>
    );
};

const TenantsPage = () => {
    const { loading: loadingApps, error: appsError, selectedAppId: appId } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [formData, setFormData] = useState({
        tenant_name: '',
        tenant_config: '{}',
        admin_username: '',
    });
    const [createTenantStep, setCreateTenantStep] = useState(1);
    const [userSelectionMode, setUserSelectionMode] = useState('existing');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    // Tenant admin candidates: any active user, loaded when the "existing user" step is shown.
    const usersQuery = useAppUsers(ALL_APPS, {
        enabled: showCreateModal && userSelectionMode === 'existing' && createTenantStep === 2,
    });
    const allUsers = usersQuery.items;
    const [newUserFormData, setNewUserFormData] = useState({
        username: '',
        email_id: '',
        first_name: '',
        last_name: '',
        phone_no: '',
        gender: '',
    });

    // URL keys: status, created_from/created_to.
    const columns = [
        {
            key: 'tenant_name',
            header: 'Tenant',
            sortKey: 'name',
            locked: true,
            render: (t) => <span className="font-medium text-slate-900">{t.tenant_name}</span>,
        },
        { key: 'tenant_id', header: 'Tenant ID', sortKey: 'id', render: (t) => <CopyableId value={t.tenant_id} /> },
        { key: 'app_id', header: 'App ID', render: (t) => <CopyableId value={t.app_id} /> },
        {
            key: 'tenant_config',
            header: 'Config',
            render: (t) => {
                const text = configText(t.tenant_config);
                return (
                    <div className="max-w-xs truncate font-mono text-xs text-slate-500" title={text}>
                        {text}
                    </div>
                );
            },
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (t) => <StatusBadge active={Boolean(t.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'created',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (t) => (t.created_on ? formatDate(t.created_on) : '—'),
            filter: { type: 'date' },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (t) => (
                <RowActions>
                    <RowAction
                        icon={ENTITY_ICONS.users}
                        label="View users"
                        tone="info"
                        onClick={() =>
                            navigate(
                                `/users?app_id=${encodeURIComponent(t.app_id)}&tenant=${encodeURIComponent(
                                    t.tenant_id
                                )}`
                            )
                        }
                    />
                    <RowAction icon={Edit2} label="Edit tenant" onClick={() => openUpdateModal(t)} />
                    <RowAction
                        icon={Power}
                        label={t.is_active ? 'Deactivate tenant' : 'Activate tenant'}
                        tone={t.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(t, t.is_active)}
                    />
                    <RowAction
                        icon={Trash2}
                        label="Delete tenant"
                        tone="danger"
                        onClick={() => handleDeleteTenant(t)}
                    />
                </RowActions>
            ),
        },
    ];

    // Disabled tenants are listed too (so they can be re-enabled); filter by status to narrow.
    const table = useServerTable((query) => tenantsService.getPage(appId.trim(), query), {
        deps: [appId],
        enabled: Boolean(appId && appId.trim()),
        defaultSort: { key: 'name', dir: 'asc' },
        columns,
    });

    // A tenant picked under the previous app means nothing in the new one.
    useEffect(() => {
        setSelectedTenant(null);
        setShowUpdateModal(false);
    }, [appId]);

    const filteredUsers = useMemo(() => {
        if (!userSearchTerm) return allUsers;
        const search = userSearchTerm.toLowerCase();
        return allUsers.filter(
            (user) =>
                user.username?.toLowerCase().includes(search) ||
                user.email_id?.toLowerCase().includes(search) ||
                `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(search)
        );
    }, [allUsers, userSearchTerm]);

    const userOptions = useMemo(() => {
        return filteredUsers.map((user) => ({
            value: user.username,
            label: user.username,
            subtitle: `${user.first_name || ''} ${user.last_name || ''} ${
                user.email_id ? `(${user.email_id})` : ''
            }`.trim(),
        }));
    }, [filteredUsers]);

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        setError('');
        try {
            if (userSelectionMode === 'existing' && !formData.admin_username) {
                setError('Please select an existing user as tenant admin');
                return;
            }

            if (userSelectionMode === 'new') {
                if (
                    !newUserFormData.username ||
                    !newUserFormData.email_id ||
                    !newUserFormData.first_name ||
                    !newUserFormData.last_name ||
                    !newUserFormData.phone_no ||
                    !newUserFormData.gender
                ) {
                    setError('Please fill in all required fields for the new admin user');
                    return;
                }
            }

            // One request (AIQ-451): the server creates the tenant and its Admin together,
            // so a failure never leaves a tenant without an administrator.
            const payload = {
                tenant_name: formData.tenant_name,
                app_id: appId,
                tenant_config: formData.tenant_config ? JSON.parse(formData.tenant_config) : null,
            };
            if (userSelectionMode === 'existing') {
                payload.admin_username = formData.admin_username;
            } else if (userSelectionMode === 'new') {
                payload.admin_user = {
                    username: newUserFormData.username,
                    email_id: newUserFormData.email_id,
                    first_name: newUserFormData.first_name,
                    last_name: newUserFormData.last_name,
                    phone_no: newUserFormData.phone_no,
                    gender: newUserFormData.gender,
                };
            }

            await tenantsService.create(payload);
            setShowCreateModal(false);
            resetForm();
            table.reload();
            notice.success(`Tenant ${formData.tenant_name} created.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create tenant');
        }
    };

    const handleUpdateTenant = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;
        setError('');
        try {
            await tenantsService.updateConfig(selectedTenant.tenant_id, JSON.parse(formData.tenant_config || '{}'));
            setShowUpdateModal(false);
            setSelectedTenant(null);
            resetForm();
            table.reload();
            notice.success(`Configuration of ${selectedTenant.tenant_name} updated.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update tenant');
        }
    };

    const handleDeleteTenant = async (tenant) => {
        const ok = await confirm({
            title: `Delete tenant ${tenant.tenant_name}?`,
            body: 'The tenant and everything scoped to it are permanently deleted. This can’t be undone.',
            impact: [
                'All user mappings for this tenant',
                'All user role mappings for this tenant',
                'All authentication sessions for this tenant',
            ],
            confirmLabel: 'Delete',
            tone: 'danger',
            typeToConfirm: tenant.tenant_name,
        });
        if (!ok) return;
        try {
            await tenantsService.deleteTenant(tenant.tenant_id);
            table.reload();
            notice.success(`Tenant ${tenant.tenant_name} deleted.`);
        } catch (err) {
            notice.error(err, `Couldn’t delete tenant ${tenant.tenant_name}. Try again.`);
        }
    };

    const handleToggleStatus = async (tenant, currentStatus) => {
        const verb = currentStatus ? 'deactivate' : 'activate';
        const ok = await confirm({
            title: `${currentStatus ? 'Deactivate' : 'Activate'} tenant ${tenant.tenant_name}?`,
            body: currentStatus
                ? 'The tenant is unavailable until it is activated again. Its users and data are kept.'
                : 'The tenant becomes available again with its existing users and data.',
            confirmLabel: currentStatus ? 'Deactivate' : 'Activate',
            tone: currentStatus ? 'danger' : 'default',
        });
        if (!ok) return;
        try {
            await tenantsService.updateStatus(tenant.tenant_id, currentStatus ? 0 : 1);
            table.reload();
            notice.success(`Tenant ${tenant.tenant_name} ${verb}d.`);
        } catch (err) {
            notice.error(err, `Couldn’t ${verb} ${tenant.tenant_name}. Try again.`);
        }
    };

    const resetForm = () => {
        setFormData({
            tenant_name: '',
            tenant_config: '{}',
            admin_username: '',
        });
        setUserSelectionMode('existing');
        setNewUserFormData({
            username: '',
            email_id: '',
            first_name: '',
            last_name: '',
            phone_no: '',
            gender: '',
        });
        setUserSearchTerm('');
        setCreateTenantStep(1);
    };

    const openUpdateModal = (tenant) => {
        setError('');
        setSelectedTenant(tenant);
        setFormData({
            tenant_config: JSON.stringify(tenant.tenant_config || {}, null, 2),
        });
        setShowUpdateModal(true);
    };


    return (
        <div className="space-y-4">
            <PageHeader
                title="Tenants"
                actions={
                    appId && (
                        <Button
                            onClick={() => {
                                resetForm();
                                setError('');
                                setCreateTenantStep(1);
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" /> Add Tenant
                        </Button>
                    )
                }
            />

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its tenants.</p>
                </div>
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="tenants"
                    rowKey="tenant_id"
                    searchPlaceholder="Search name, ID, config…"
                    emptyMessage="No tenants found for the selected app."
                />
            )}

            {/* Create Tenant Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => {
                    resetForm();
                    setShowCreateModal(false);
                }}
                title="Create New Tenant"
                size="lg"
            >
                {/* Step Indicator */}
                <div className="flex items-center justify-center mb-6">
                    <div className="flex items-center">
                        <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                createTenantStep >= 1 ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
                            }`}
                        >
                            1
                        </div>
                        <div className={`w-16 h-1 mx-2 ${createTenantStep >= 2 ? 'bg-primary-600' : 'bg-slate-200'}`} />
                        <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                createTenantStep >= 2 ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
                            }`}
                        >
                            2
                        </div>
                    </div>
                </div>
                <div className="text-center mb-6">
                    <p className="text-sm text-slate-600">
                        {createTenantStep === 1 ? 'Step 1: Tenant Details' : 'Step 2: Tenant Admin User'}
                    </p>
                </div>

                <form onSubmit={handleCreateTenant} className="flex flex-col min-h-[400px]">
                    <div className="flex-1 space-y-4">
                        {/* Step 1: Tenant Details */}
                        {createTenantStep === 1 && (
                            <>
                                <Field label="Tenant Name *">
                                    <TextInput
                                        type="text"
                                        required
                                        value={formData.tenant_name}
                                        onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                                        placeholder="Tenant A"
                                    />
                                </Field>

                                <Field label="Tenant Config (JSON)">
                                    <Textarea
                                        rows={4}
                                        value={formData.tenant_config}
                                        onChange={(e) => setFormData({ ...formData, tenant_config: e.target.value })}
                                        className="font-mono"
                                        placeholder='{"custom_field": "value"}'
                                    />
                                </Field>

                                {error && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                        {error}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Step 2: Tenant Admin User */}
                        {createTenantStep === 2 && (
                            <>
                                <div>
                                    <p className="text-sm font-medium text-slate-700 mb-3">Tenant Admin User</p>

                                    <div className="flex gap-4 mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tenantAdminUserMode"
                                                value="existing"
                                                checked={userSelectionMode === 'existing'}
                                                onChange={(e) => {
                                                    setUserSelectionMode(e.target.value);
                                                    setFormData({ ...formData, admin_username: '' });
                                                    setNewUserFormData({
                                                        username: '',
                                                        email_id: '',
                                                        first_name: '',
                                                        last_name: '',
                                                        phone_no: '',
                                                        gender: '',
                                                    });
                                                }}
                                                className="text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-slate-700">Existing User</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tenantAdminUserMode"
                                                value="new"
                                                checked={userSelectionMode === 'new'}
                                                onChange={(e) => {
                                                    setUserSelectionMode(e.target.value);
                                                    setFormData({ ...formData, admin_username: '' });
                                                }}
                                                className="text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-slate-700">New User</span>
                                        </label>
                                    </div>

                                    {userSelectionMode === 'existing' ? (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Select Existing User <span className="text-red-500">*</span>
                                            </label>
                                            <Typeahead
                                                options={userOptions}
                                                value={formData.admin_username}
                                                onChange={(value) =>
                                                    setFormData({ ...formData, admin_username: value || '' })
                                                }
                                                onSearch={(term) => setUserSearchTerm(term)}
                                                placeholder="Search by username, email, or name..."
                                                displayKey="label"
                                                valueKey="value"
                                                loading={usersQuery.loading}
                                            />
                                            {usersQuery.error && (
                                                <p className="text-xs text-red-600 mt-1.5">{usersQuery.error}</p>
                                            )}
                                            {formData.admin_username && (
                                                <p className="text-xs text-slate-500 mt-1.5">
                                                    Selected: {formData.admin_username}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <NewUserForm
                                            formData={newUserFormData}
                                            setFormData={setNewUserFormData}
                                            requireContact
                                        />
                                    )}
                                </div>

                                {error && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                        {error}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer buttons */}
                    <div className="flex gap-3 pt-4 mt-auto border-t border-slate-200">
                        {createTenantStep === 1 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!formData.tenant_name.trim()) {
                                            setError('Tenant name is required');
                                            return;
                                        }
                                        if (formData.tenant_config) {
                                            try {
                                                JSON.parse(formData.tenant_config);
                                            } catch {
                                                setError('Tenant config must be valid JSON');
                                                return;
                                            }
                                        }
                                        setError('');
                                        setCreateTenantStep(2);
                                    }}
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                                >
                                    Next: Admin User
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCreateTenantStep(1);
                                        setError('');
                                    }}
                                    className="px-4 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                                >
                                    Create Tenant
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </Modal>

            {/* Update Tenant Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => setShowUpdateModal(false)}
                title={`Update ${selectedTenant?.tenant_name || 'Tenant'}`}
                size="md"
            >
                <form onSubmit={handleUpdateTenant} className="space-y-4">
                    <TenantOverridesEditor
                        tenantConfigStr={formData.tenant_config}
                        onChange={(str) => setFormData({ ...formData, tenant_config: str })}
                        appId={appId}
                        tenantId={selectedTenant?.tenant_id}
                    />

                    <Field label="Tenant Config (JSON)">
                        <Textarea
                            rows={6}
                            value={formData.tenant_config}
                            onChange={(e) => setFormData({ ...formData, tenant_config: e.target.value })}
                            className="font-mono"
                            placeholder='{"key": "value"}'
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update Tenant
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowUpdateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default TenantsPage;
